import { useStore } from '@nanostores/react';
import { memo, type ReactNode } from 'react';
import { getDatabaseComponent } from '~/components/dialogs/settings/SettingsDialog/helpers';
import { useSheetNavigation } from '~/lib/context/sheetNavigation';
import { SettingsPage } from '~/lib/stores/menu';
import { supabaseStore } from '~/lib/stores/supabase';
import { classNames } from '~/utils/classNames';

type DatabaseMenuItem = { id: SettingsPage; text: string };

export const DatabaseMenuSection = memo(() => {
  const { stack, push, reset } = useSheetNavigation();
  const supabaseIntegration = useStore(supabaseStore.supabaseProjectIntegration);
  const isBoltNative = supabaseIntegration?.isBoltNative;

  const CONFIG: DatabaseMenuItem[] = [
    {
      id: SettingsPage.DatabaseGeneral,
      text: 'Tables',
    },
    {
      id: SettingsPage.DatabaseLogs,
      text: 'Logs',
    },
    {
      id: SettingsPage.DatabaseSuggestions,
      text: 'Security Audit',
    },
    ...(isBoltNative
      ? [
          {
            id: SettingsPage.DatabaseAdvanced,
            text: 'Advanced',
          },
        ]
      : []),
  ];

  const handleTopLevelNavigation = (title: string, component: ReactNode) => {
    if (stack.length === 1 && stack[0].title === title) {
      return;
    }

    reset();
    push({ title, component });
  };

  const currentTab = stack.length > 0 ? stack[0].title : null;

  return (
    <div className="flex gap-2 items-center bg-bolt-ds-surface sticky top-0 z-layer-2 py-4 px-6 border-b border-bolt-ds-borderOutline">
      {CONFIG.map((item) => (
        <div key={item.id}>
          <DatabaseSettingTab
            menuItem={item}
            currentTab={currentTab}
            onChooseTab={() => {
              handleTopLevelNavigation(item.text, getDatabaseComponent(item.id));
            }}
          />
        </div>
      ))}
    </div>
  );
});

interface DatabaseSettingTabProps {
  menuItem: DatabaseMenuItem;
  currentTab: string | null;
  onChooseTab: (currentPage: SettingsPage) => void;
}

const DatabaseSettingTab = memo(({ menuItem, currentTab, onChooseTab }: DatabaseSettingTabProps) => {
  if (!currentTab) {
    return null;
  }

  const isActive = currentTab === menuItem.text;

  return (
    <button
      key={menuItem.id}
      className={classNames('text-center px-3 py-1 rounded-full text-sm font-medium', {
        'bg-bolt-ds-brandContainer text-bolt-ds-brand': isActive,
        'bg-transparent hover:bg-bolt-ds-surfaceHighlight text-bolt-ds-textSecondary': !isActive,
      })}
      onClick={() => onChooseTab(menuItem.id)}
    >
      {menuItem.text}
    </button>
  );
});
